import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {useEffect, useMemo, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import {serverLocalName} from 'GLOBAL/envVariables'
import {getAllOfficeList} from 'utils/apiCalling/api'
import useOfficeHierarchy from 'utils/hooks/useOfficeHierarchy'
import {
  ALL_OFFICES,
  SERVER_CYCLE_CONFIG_PATH,
  SERVER_CYCLE_PAGES,
  ServerCycleConfig,
  ServerCyclePage,
  defaultServerCycleConfig,
  getServerCycleConfigStatus,
  loadServerCycleConfig,
  parseServerCycleConfig,
  stringifyServerCycleConfig,
} from 'utils/config/serverCycleConfig'
import {confirmTextAlert, normalAlert, resultAlert} from 'utils/CustomAlert/Alert'

/** nginx .htpasswd 에 등록한 계정명. 비밀번호는 코드에 두지 않는다. */
const CONFIG_AUTH_USER = 'admin'

/**
 * 편집 중에는 선택지를 배열로 다룬다.
 * 저장 형식은 `{주기: 라벨}` 객체지만, 주기(키)를 고치는 중에는 값이 잠시 비거나
 * 겹치기 마련이라 객체로 들고 있으면 입력 도중 행이 사라진다.
 */
interface OptionDraft {
  cycle: string
  label: string
}

interface OfficeDraft {
  officeId: string
  label: string
  pages: ServerCyclePage[]
  options: OptionDraft[]
}

/** 화면·선택지만 담는 편집 단위 */
type CycleDraft = Pick<OfficeDraft, 'pages' | 'options'>

/* 사업소마다 배열을 새로 만든다. 상수 하나를 나눠 쓰면 나중에 제자리 수정이 섞일 때 함께 바뀐다. */
const newCycleDraft = (): CycleDraft => ({pages: ['info'], options: [{cycle: '', label: ''}]})

/* 탭 이름은 짧아야 한 줄에 많이 들어간다. 코드는 아직 안 고른 줄에서만 보여준다. */
const tabLabel = (draft: OfficeDraft) => {
  if (draft.officeId === '') return '새 사업소'
  if (draft.officeId === ALL_OFFICES) return '전체'
  return draft.label || draft.officeId
}

/**
 * 이 사업소에 적용될 줄. 어느 탭을 고쳐야 할지 바로 보이게 한다.
 * 실제 판정과 어긋나면 안 되므로 matchServerCycleOffice 와 같은 규칙으로 고른다.
 */
const findMatchedIndex = (drafts: OfficeDraft[], officeId: string): number => {
  const candidates = drafts
    .map((draft, index) => ({id: draft.officeId.trim(), index}))
    .filter(({id}) => id !== ALL_OFFICES && id !== '' && officeId.startsWith(id))
    .sort((a, b) => b.id.length - a.id.length)
  if (candidates.length > 0) return candidates[0].index
  return drafts.findIndex((draft) => draft.officeId.trim() === ALL_OFFICES)
}

/** 주기만 입력하면 라벨은 대개 "N시간"이라 자동으로 채운다. */
const autoLabel = (cycle: string) => (cycle.trim() === '' ? '' : `${cycle.trim()}시간`)

/** 저장 형식의 순서를 화면 순서와 맞춰 diff 를 읽기 쉽게 둔다. */
const togglePageIn = (pages: ServerCyclePage[], page: ServerCyclePage): ServerCyclePage[] =>
  pages.includes(page)
    ? pages.filter((p) => p !== page)
    : SERVER_CYCLE_PAGES.filter(({id}) => id === page || pages.includes(id)).map(({id}) => id)

const toDrafts = (config: ServerCycleConfig): OfficeDraft[] =>
  config.offices.map((office) => ({
    officeId: office.officeId,
    label: office.label,
    pages: [...office.pages],
    options: Object.entries(office.options).map(([cycle, label]) => ({cycle, label})),
  }))

const toConfig = (drafts: OfficeDraft[]): ServerCycleConfig => ({
  offices: drafts.map((draft) => ({
    officeId: draft.officeId.trim(),
    label: draft.label.trim(),
    pages: draft.pages,
    options: draft.options.reduce<Record<string, string>>((acc, {cycle, label}) => {
      acc[cycle.trim()] = label.trim()
      return acc
    }, {}),
  })),
})

interface CycleDraftEditorProps {
  draft: CycleDraft
  onChange: (patch: Partial<CycleDraft>) => void
  /** 화면 노출 설명은 사업소별 편집과 일괄 수정에서 다르게 읽힌다 */
  pagesHint: string
}

/** 노출 화면과 선택지 편집. 사업소 탭과 일괄 수정 탭이 함께 쓴다. */
const CycleDraftEditor = ({draft, onChange, pagesHint}: CycleDraftEditorProps) => {
  const updateOption = (index: number, patch: Partial<OptionDraft>) =>
    onChange({
      options: draft.options.map((option, i) => {
        if (i !== index) return option
        const next = {...option, ...patch}
        /* 라벨을 따로 손대지 않았다면 주기를 바꿀 때 같이 따라오게 한다 */
        if (patch.cycle !== undefined && option.label === autoLabel(option.cycle)) {
          next.label = autoLabel(patch.cycle)
        }
        return next
      }),
    })

  return (
    <>
      <div className={'mb-5'}>
        <h4 className={'fw-bold fs-5 mb-1'}>필터를 노출할 화면</h4>
        <p className={'text-muted fs-6 mb-2'}>{pagesHint}</p>
        <div className={'d-flex flex-wrap gap-4'}>
          {SERVER_CYCLE_PAGES.map((page) => (
            <FormControlLabel
              key={page.id}
              control={
                <Checkbox
                  checked={draft.pages.includes(page.id)}
                  onChange={() => onChange({pages: togglePageIn(draft.pages, page.id)})}
                />
              }
              label={page.label}
            />
          ))}
        </div>
      </div>

      <div>
        <h4 className={'fw-bold fs-5 mb-1'}>통신주기 선택지</h4>
        <p className={'text-muted fs-6 mb-2'}>
          주기는 시간 단위 정수입니다. 목록에 넣은 순서대로 화면에 나옵니다.
        </p>
        {draft.options.map((option, index) => (
          <div key={`option-${index}`} className={'d-flex flex-wrap gap-3 align-items-center mb-2'}>
            <TextField
              size={'small'}
              type={'number'}
              label={'주기(시간)'}
              value={option.cycle}
              onChange={(e) => updateOption(index, {cycle: e.target.value})}
              sx={{width: 140}}
            />
            <TextField
              size={'small'}
              label={'화면에 보일 문구'}
              value={option.label}
              placeholder={autoLabel(option.cycle) || '6시간'}
              onChange={(e) => updateOption(index, {label: e.target.value})}
              sx={{width: 220}}
            />
            <IconButton
              onClick={() => onChange({options: draft.options.filter((_, i) => i !== index)})}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </div>
        ))}
        <Button
          variant={'outlined'}
          color={'inherit'}
          onClick={() => onChange({options: [...draft.options, {cycle: '', label: ''}]})}
        >
          주기 추가
        </Button>
      </div>
    </>
  )
}

const ServerCycleConfigPage = () => {
  const officeInfo = useSelector((state: any) => state?.officeInfo)
  const userInfo = useSelector((state: any) => state.userInfo?.userInfo)
  const [officeList, setOfficeList] = useState<any[]>([])
  const [drafts, setDrafts] = useState<OfficeDraft[]>([])
  /* 사업소가 늘어나도 한 번에 한 곳만 보이도록 탭으로 나눈다 */
  const [tab, setTab] = useState(0)
  const [status, setStatus] = useState(getServerCycleConfigStatus())
  const [saving, setSaving] = useState(false)
  const [authOpen, setAuthOpen] = useState(false)
  const [password, setPassword] = useState('')
  /* 저장은 nginx Basic 인증을 통과해야 한다.
     비밀번호를 코드나 localStorage 에 두면 인증 의미가 없으므로,
     저장할 때 입력받아 같은 화면에 머무는 동안만 메모리에 들고 있는다. */
  const credentialRef = useRef<string | null>(null)

  const refresh = async () => {
    const loaded = toDrafts(await loadServerCycleConfig())
    setDrafts(loaded)
    setStatus(getServerCycleConfigStatus())
    /* 대개 지금 접속한 사업소를 고치러 들어오므로 그 탭을 먼저 연다 */
    const matched = findMatchedIndex(loaded, String(officeInfo?.value ?? ''))
    setTab(matched === -1 ? 0 : matched)
  }

  useEffect(() => {
    refresh()
    /* 사업소를 코드로 외우게 하지 않으려면 목록이 필요하다.
       목록을 못 받아도 저장된 코드는 그대로 보이므로 편집 자체는 막지 않는다. */
    getAllOfficeList(userInfo, {
      success: (res: any) => setOfficeList(res ?? []),
      fail: () => setOfficeList([]),
    })
  }, [])

  /* `전체` 라는 이름의 항목은 계층 계산에서 빠지고 목록 맨 위에 고정된다(officeHierarchy 규칙) */
  const officeOptions = useMemo(
    () => [{officeId: ALL_OFFICES, officeName: '전체'}, ...officeList],
    [officeList]
  )

  /** 사업소 코드로도 검색되도록 표시 문자열을 그대로 검색 대상으로 넘긴다 */
  const officeOptionLabel = (option: any) =>
    !option || option.officeId === ALL_OFFICES
      ? '전체'
      : `${option.officeName ?? ''} (${option.officeId ?? ''})`

  const officeHierarchy = useOfficeHierarchy(officeOptions, {getLabel: officeOptionLabel})

  /**
   * 목록에 없는 코드(다른 서버에서 만든 설정 등)를 골랐던 자리도 값을 잃지 않도록
   * 저장된 코드를 그대로 항목처럼 만들어 돌려준다.
   */
  const optionOf = (draft: OfficeDraft) => {
    if (draft.officeId === '') return null
    return (
      officeOptions.find((option: any) => option.officeId === draft.officeId) ?? {
        officeId: draft.officeId,
        officeName: draft.label || '목록에 없는 사업소',
      }
    )
  }

  /** 이 코드가 실제로 몇 곳을 덮는지. 본부를 골랐는지 사업소를 골랐는지 바로 보이게 한다. */
  const coverageText = (officeId: string) => {
    if (officeId === ALL_OFFICES) return '이 서버에 붙는 모든 사업소에 적용됩니다.'
    if (officeId === '') return '사업소를 선택해주세요.'
    const covered = officeList.filter((office: any) =>
      String(office?.officeId ?? '').startsWith(officeId)
    )
    if (covered.length <= 1) return `${officeId} 사업소에만 적용됩니다.`
    return `${officeId} 로 시작하는 하위 사업소까지 ${covered.length}곳에 적용됩니다.`
  }

  const updateOffice = (index: number, patch: Partial<OfficeDraft>) =>
    setDrafts((prev) => prev.map((draft, i) => (i === index ? {...draft, ...patch} : draft)))

  const addOffice = () => {
    setDrafts((prev) => [...prev, {officeId: '', label: '', ...newCycleDraft()}])
    /* 방금 추가한 곳을 바로 편집하게 한다 */
    setTab(drafts.length)
  }

  /**
   * 사업소를 목록에서 뺀다.
   *
   * 저장하면 그 사업소에서 필터가 사라지는데 되돌릴 방법이 없으므로 한 번 묻는다.
   * 탭의 작은 × 를 잘못 눌러 조용히 지워지는 쪽이 더 위험하다.
   */
  const removeOffice = async (index: number) => {
    const target = drafts[index]
    if (!target) return

    const confirmed = await confirmTextAlert(
      `${tabLabel(target)} 을(를) 목록에서 뺍니다.<br/>저장하면 이 사업소에서 통신주기 필터가 사라집니다.`,
      '삭제'
    )
    if (!confirmed) return

    setDrafts((prev) => prev.filter((_, i) => i !== index))
    /* 지운 탭에 머물러 빈 화면을 보지 않도록 앞 탭으로 물러난다 */
    setTab((prev) => (prev >= index && prev > 0 ? prev - 1 : prev))
  }

  /** 저장 전 검증. 화면에서 만든 값이라도 빈 주기나 중복 사업소 같은 실수는 걸러야 한다. */
  const validate = (): string | null => {
    const parsed = parseServerCycleConfig(JSON.parse(stringifyServerCycleConfig(toConfig(drafts))))
    return typeof parsed === 'string' ? parsed : null
  }

  const download = () => {
    const error = validate()
    if (error) {
      normalAlert(error)
      return
    }
    const blob = new Blob([stringifyServerCycleConfig(toConfig(drafts))], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'server-cycle-config.json'
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  /** 저장 버튼. 비밀번호가 아직 없으면 먼저 입력받는다. */
  const requestSave = () => {
    const error = validate()
    if (error) {
      normalAlert(error)
      return
    }
    if (!credentialRef.current) {
      setAuthOpen(true)
      return
    }
    save()
  }

  const confirmPassword = () => {
    try {
      credentialRef.current = btoa(`${CONFIG_AUTH_USER}:${password}`)
    } catch {
      /* btoa 는 한글 등 비 ASCII 를 처리하지 못한다 */
      normalAlert('비밀번호는 영문·숫자·기호만 사용할 수 있습니다.')
      return
    }
    setPassword('')
    setAuthOpen(false)
    save()
  }

  const save = async () => {
    setSaving(true)
    try {
      const res = await fetch(SERVER_CYCLE_CONFIG_PATH, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${credentialRef.current}`,
        },
        body: stringifyServerCycleConfig(toConfig(drafts)),
      })

      if (res.status === 401) {
        /* 잘못된 비밀번호를 계속 들고 있으면 다음 저장도 막히므로 버린다 */
        credentialRef.current = null
        await normalAlert('비밀번호가 올바르지 않습니다. 다시 시도해주세요.')
        return
      }
      if (res.status === 403) {
        /* 403 은 인증은 통과했는데 쓰기가 거부된 것이다. 비밀번호를 다시 묻게 하면
           맞는 비밀번호를 계속 의심하게 되므로 원인을 그대로 알려준다. */
        await normalAlert(
          '서버가 이 파일 쓰기를 거부했습니다. (HTTP 403)\n비밀번호 문제가 아니라 저장 경로 권한 설정을 확인해야 합니다.'
        )
        return
      }
      if (!res.ok) {
        await normalAlert(
          `저장하지 못했습니다. (HTTP ${res.status})\n서버에 쓰기 설정이 되어 있는지 확인해주세요.`
        )
        return
      }

      /* PUT 응답만 믿지 않고 다시 읽어 실제 반영을 확인한다 */
      await refresh()
      await resultAlert({resultCode: 0})
    } catch {
      await normalAlert('저장 요청에 실패했습니다. 네트워크 상태를 확인해주세요.')
    } finally {
      setSaving(false)
    }
  }

  const resetToDefault = () => {
    setDrafts(toDrafts(defaultServerCycleConfig(serverLocalName)))
    setTab(0)
  }

  const matchedIndex = findMatchedIndex(drafts, String(officeInfo?.value ?? ''))

  /**
   * 같은 사업소를 가리키는 탭들.
   *
   * 매칭은 먼저 나온 줄만 쓰므로 뒤쪽 탭은 아무 효과가 없다. 저장할 때도 거부되지만,
   * 그때는 이미 여러 탭을 채운 뒤라 만드는 시점에 알려준다.
   */
  const duplicatedIds = useMemo(() => {
    const counts = new Map<string, number>()
    drafts.forEach((draft) => {
      const id = draft.officeId.trim()
      if (id !== '') counts.set(id, (counts.get(id) ?? 0) + 1)
    })
    return new Set(
      Array.from(counts)
        .filter(([, count]) => count > 1)
        .map(([id]) => id)
    )
  }, [drafts])

  const isDuplicated = (draft: OfficeDraft) => duplicatedIds.has(draft.officeId.trim())

  /* 사업소를 지우면 탭 번호가 밀리므로 범위를 벗어난 값은 첫 탭으로 되돌린다 */
  const activeTab = tab >= drafts.length ? 0 : tab
  const activeDraft = drafts[activeTab]

  return (
    <div className={'card card-body'}>
      {/* 서버에 직접 접속할 수 없는 환경이라, 지금 무엇이 적용 중인지 화면이 말해줘야 한다 */}
      {status.source === 'remote' ? (
        <Alert severity={'success'} className={'mb-4 fs-5'}>
          설정 파일이 적용 중입니다.
          {status.loadedAt && ` (확인 시각 ${status.loadedAt.toLocaleString('ko-KR')})`}
        </Alert>
      ) : (
        <Alert severity={'warning'} className={'mb-4 fs-5'}>
          기본값으로 동작 중입니다. {status.error ?? '설정 파일이 없습니다.'}
        </Alert>
      )}

      <Alert severity={'info'} className={'mb-4 fs-6'}>
        저장하면 <b>탭에 있는 사업소가 전부</b>가 됩니다. 탭에서 뺀 사업소는 필터가 사라지니, 다른
        사업소 탭을 실수로 지우지 않도록 주의해주세요.
        <br />
        지금 접속한 사업소는 <b>{officeInfo?.value ?? '-'}</b> ({serverLocalName} 서버)
        {matchedIndex === -1 && ' — 해당하는 탭이 없어 필터가 노출되지 않습니다.'}
      </Alert>

      <div className={'d-flex align-items-center gap-3 border-bottom mb-5'}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setTab(value)}
          variant={'scrollable'}
          scrollButtons={'auto'}
          sx={{flex: 1, minHeight: 0}}
        >
          {drafts.map((draft, index) => (
            <Tab
              key={`tab-${index}`}
              value={index}
              label={
                <span
                  className={'d-flex align-items-center gap-2'}
                  style={isDuplicated(draft) ? {color: '#d32f2f'} : undefined}
                >
                  {isDuplicated(draft) && '⚠ '}
                  {tabLabel(draft)}
                  {index === matchedIndex && (
                    <span className={'badge badge-primary fs-8'}>접속</span>
                  )}
                  {/* Tab 자체가 button 이라 버튼을 겹쳐 넣을 수 없어 span 으로 만든다
                      (useOfficeHierarchy 의 펼침 화살표와 같은 방식) */}
                  <Tooltip title={'이 사업소 삭제'}>
                    <span
                      style={{color: '#aaa', cursor: 'pointer', padding: '0 4px'}}
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        removeOffice(index)
                      }}
                    >
                      ✕
                    </span>
                  </Tooltip>
                </span>
              }
            />
          ))}
        </Tabs>
        <Button variant={'outlined'} size={'small'} onClick={addOffice} sx={{flexShrink: 0}}>
          사업소 추가
        </Button>
      </div>

      {activeDraft ? (
        <div className={'mb-5'}>
          {isDuplicated(activeDraft) && (
            <Alert severity={'error'} className={'mb-4 fs-6'}>
              같은 사업소를 가리키는 탭이 또 있습니다. 먼저 나온 탭만 적용되고 나머지는 무시되므로,
              하나만 남기고 지워야 저장할 수 있습니다.
              <br />한 사업소 안에서 화면마다 다르게 두고 싶다면 탭을 나누지 말고, 이 탭의 노출
              화면 체크박스로 조절해주세요.
            </Alert>
          )}
          <div className={'mb-2'}>
            <Autocomplete
              options={officeHierarchy.options}
              filterOptions={officeHierarchy.filterOptions}
              renderOption={officeHierarchy.renderOption}
              getOptionLabel={officeOptionLabel}
              isOptionEqualToValue={(option: any, value: any) =>
                option?.officeId === value?.officeId
              }
              value={optionOf(activeDraft)}
              noOptionsText={'검색 결과가 없습니다.'}
              onChange={(_, selected: any) =>
                updateOffice(activeTab, {
                  officeId: selected?.officeId ?? '',
                  label: selected?.officeName ?? '',
                })
              }
              sx={{width: 360}}
              renderInput={(params) => <TextField {...params} size={'small'} label={'사업소'} />}
            />
          </div>
          <p className={'text-muted fs-6 mb-5'}>{coverageText(activeDraft.officeId.trim())}</p>

          <CycleDraftEditor
            draft={activeDraft}
            onChange={(patch) => updateOffice(activeTab, patch)}
            pagesHint={'모두 끄면 이 사업소에는 필터가 나오지 않습니다.'}
          />
        </div>
      ) : (
        <div className={'text-center text-muted fs-5 py-10 mb-5'}>
          등록된 사업소가 없습니다. 이대로 저장하면 어느 사업소에서도 통신주기 필터가 나오지
          않습니다.
        </div>
      )}

      <div className={'d-flex flex-wrap gap-3 border-top pt-4'}>
        <Button variant={'contained'} onClick={requestSave} disabled={saving}>
          {saving ? '저장 중...' : '저장'}
        </Button>
        <Button variant={'outlined'} onClick={download}>
          JSON 다운로드
        </Button>
        <Button variant={'outlined'} color={'inherit'} onClick={refresh}>
          다시 불러오기
        </Button>
        <Button variant={'text'} color={'inherit'} onClick={resetToDefault}>
          기본값으로 되돌리기
        </Button>
      </div>

      <Dialog open={authOpen} onClose={() => setAuthOpen(false)} fullWidth maxWidth={'xs'}>
        <DialogTitle className={'fw-bold'}>저장 비밀번호</DialogTitle>
        <DialogContent>
          <p className={'text-muted fs-6 mb-3'}>
            설정을 저장하려면 비밀번호가 필요합니다. 이 화면을 벗어나면 다시 입력해야 합니다.
          </p>
          <TextField
            autoFocus
            fullWidth
            type={'password'}
            label={'비밀번호'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmPassword()
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button color={'inherit'} onClick={() => setAuthOpen(false)}>
            취소
          </Button>
          <Button variant={'contained'} onClick={confirmPassword} disabled={password === ''}>
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default ServerCycleConfigPage
